import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getReplacementDemandShiftTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import { ReplacementDemandShiftSummaryBar } from './replacementDemandShiftComponents/ReplacementDemandShiftSummaryBar';
import { ReplacementDemandShiftRecipientRail } from './replacementDemandShiftComponents/ReplacementDemandShiftRecipientRail';
import { ReplacementDemandShiftMainTable } from './replacementDemandShiftComponents/ReplacementDemandShiftMainTable';
import {
  buildReplacementDemandRecipientGroups,
  filterReplacementDemandRows,
  summarizeReplacementDemandRows,
} from '../../lib/replacementDemandShiftHelpers';

const ReplacementDemandShiftTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
}) => {
  const dispatch = useDispatch();
  const { replacementDemandShiftTableData, loadingKeys } = useSelector(state => state.demandPlanning);

  const [localSearch, setLocalSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [exceptionsOnly, setExceptionsOnly] = useState(false);

  const replacementDemandShiftTableLoading = loadingKeys.includes(
    DemandPlanningLoadingKeys.getReplacementDemandShiftTableData
  );

  const rows = useMemo(() => {
    const raw = replacementDemandShiftTableData?.rows;
    return arrayHasItems(raw) ? raw : [];
  }, [replacementDemandShiftTableData]);

  const filteredRows = useMemo(
    () =>
      filterReplacementDemandRows(rows, {
        search: localSearch,
        activeOnly,
        exceptionsOnly,
      }),
    [rows, localSearch, activeOnly, exceptionsOnly]
  );

  const summary = useMemo(() => summarizeReplacementDemandRows(filteredRows), [filteredRows]);
  const recipientGroups = useMemo(
    () => buildReplacementDemandRecipientGroups(filteredRows),
    [filteredRows]
  );

  const forecastYear =
    replacementDemandShiftTableData?.forecastYear ??
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear;

  useEffect(() => {
    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;

    if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

    dispatch(
      getReplacementDemandShiftTableData({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
      })
    );
  }, [
    dispatch,
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecast_year,
    selectedForecastDemandPlanning?.forecastYear,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
  ]);

  if (replacementDemandShiftTableLoading) {
    return (
      <section className="flex-1 px-4 py-4 lg:px-6">
        <div className="flex justify-center items-center h-full mt-8">
          <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
        </div>
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
            Replacement Demand Shift
          </span>
          <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
            Verify direct demand movement from discontinued SKUs to their replacements
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            This surface mirrors the workbook&apos;s direct replacement layer so planners can confirm that
            sunset demand leaves the old SKU and lands on the intended replacement without creating net-new
            demand. Original units use sales from Forecast By SKU.
          </p>
        </div>
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
          <input
            className="h-9 w-full min-w-[220px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 xl:w-[260px]"
            onChange={event => setLocalSearch(event.target.value)}
            placeholder="Search original or replacement SKU"
            value={localSearch}
          />
          <label className="flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-900">
            <input
              checked={activeOnly}
              className="size-3.5 rounded border-gray-300"
              onChange={event => setActiveOnly(event.target.checked)}
              type="checkbox"
            />
            Active windows only
          </label>
          <label className="flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-900">
            <input
              checked={exceptionsOnly}
              className="size-3.5 rounded border-gray-300"
              onChange={event => setExceptionsOnly(event.target.checked)}
              type="checkbox"
            />
            Exceptions only
          </label>
        </div>
      </div>

      <ReplacementDemandShiftSummaryBar summary={summary} />

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(260px,0.82fr)_minmax(0,1.7fr)]">
        <ReplacementDemandShiftRecipientRail recipientGroups={recipientGroups} />
        <ReplacementDemandShiftMainTable rows={filteredRows} />
      </div>
    </section>
  );
};

export default ReplacementDemandShiftTable;
